import { usePersistedJsonState } from '@superfunnel/react-hooks';
import {
  AnalyticsFilters,
  CurrentStep,
  EmptyAnalyticsFilters,
} from '@superfunnel/types';
import { useCallback, useState } from 'react';

export type EditorDevice = 'desktop' | 'mobile' | 'tablet';
export type EditorMode = 'editor' | 'preview' | 'logic' | 'results';

// Re-export for convenience
export type { CurrentStep };

export interface UseHtmlStepperEditorStateProps {
  initialFormId: string;
}

export interface UseHtmlStepperEditorStateReturn {
  // Selected form ID
  selectedFormId: string;
  setSelectedFormId: (formId: string) => void;

  // Device state
  device: EditorDevice;
  setDevice: (device: EditorDevice) => void;

  // Editor mode
  editorMode: EditorMode;
  setEditorMode: (mode: EditorMode) => void;

  // Sidebar state
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Visual edit mode
  isVisualEditMode: boolean;
  setIsVisualEditMode: (mode: boolean) => void;

  // Current step (unified: controls both preview navigation and popup selection)
  currentStep: CurrentStep;
  setCurrentStep: (step: CurrentStep) => void;

  // Analytics filters (for Results tab)
  analyticsFilters: AnalyticsFilters;
  setAnalyticsFilters: (filters: AnalyticsFilters) => void;
  resetAnalyticsFilters: () => void;
}

export function useHtmlStepperEditorState({
  initialFormId,
}: UseHtmlStepperEditorStateProps): UseHtmlStepperEditorStateReturn {
  // Selected form ID
  const [selectedFormId, setSelectedFormIdInternal] = useState(initialFormId);

  // Current step (unified: controls both preview navigation and popup selection)
  const [currentStep, setCurrentStep] = useState<CurrentStep>(null);

  // When changing form, reset currentStep to null
  const setSelectedFormId = useCallback(
    (formId: string) => {
      setSelectedFormIdInternal(formId);
      setCurrentStep(null);
    },
    [setCurrentStep],
  );

  // Device state
  const [device, setDevice] = useState<EditorDevice>('desktop');

  // Editor mode
  const [editorMode, setEditorMode] = useState<EditorMode>('editor');

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Visual edit mode
  const [isVisualEditMode, setIsVisualEditMode] = useState(false);

  // Quiz-specific analytics filters (persisted separately from dynamic page filters)
  const [analyticsFilters, setAnalyticsFilters] =
    usePersistedJsonState<AnalyticsFilters>(
      'quizAnalyticsFilters',
      EmptyAnalyticsFilters,
    );

  const resetAnalyticsFilters = useCallback(() => {
    setAnalyticsFilters(EmptyAnalyticsFilters);
  }, [setAnalyticsFilters]);

  return {
    selectedFormId,
    setSelectedFormId,
    device,
    setDevice,
    editorMode,
    setEditorMode,
    sidebarCollapsed,
    setSidebarCollapsed,
    isVisualEditMode,
    setIsVisualEditMode,
    currentStep,
    setCurrentStep,
    analyticsFilters,
    setAnalyticsFilters,
    resetAnalyticsFilters,
  };
}
